/* START_MODULE_CONTRACT
 * PURPOSE: Страница входа с фирменным split-layout и формой email/пароль.
 * SCOPE: React-компонент авторизации, визуальная оболочка и submit-flow.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-AUTH
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * LoginPage - страница входа TeamPlanner.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-08-10 - UC-022: hide register link when enableRegistration=false.
 * LAST_CHANGE: 2026-07-13 - Общие AuthPage.css и AuthPageHero с register.
 * END_CHANGE_SUMMARY
 */

import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { login } from '../api/authApi';
import { useAuth } from '../context/AuthContext';
import { useFeatureToggle } from '../context/FeatureToggleContext';
import { FLAG_ENABLE_REGISTRATION } from '../features/featureToggle/featureToggleStorage';
import AuthPageHero from './AuthPageHero';
import './AuthPage.css';

/* START_METHOD_LoginPage */
/* START_CONTRACT: LoginPage
 * PURPOSE: Страница входа: форма email/пароль, сохранение сессии и редирект на /projects.
 * INPUTS: нет.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: HTTP login; authLogin (localStorage); навигация.
 * LINKS: M-FE-AUTH, V-M-FE-AUTH
 * END_CONTRACT: LoginPage
 */
export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login: authLogin } = useAuth();
  const { isEnabled, loading: flagsLoading } = useFeatureToggle();
  const navigate = useNavigate();
  const registrationEnabled = isEnabled(FLAG_ENABLE_REGISTRATION);

  /* START_METHOD_handleSubmit */
  /* START_CONTRACT: handleSubmit
   * PURPOSE: Отправить credentials, при успехе сохранить токены и перейти к проектам, иначе показать ошибку.
   * INPUTS: { e: SubmitEvent }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP login; authLogin; навигация; лог веток SUBMIT_OK/SUBMIT_FAIL.
   * LINKS: V-M-FE-AUTH
   * END_CONTRACT: handleSubmit
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await login(email, password);
      // START_BLOCK_SUBMIT_OK
      authLogin({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      navigate('/projects');
      // END_BLOCK_SUBMIT_OK
    } catch (err) {
      // START_BLOCK_SUBMIT_FAIL
      console.warn('[FeAuth][LoginPage.handleSubmit][BLOCK_SUBMIT_FAIL] login rejected');
      setError(
        err.response?.data?.message || 'Неверный email или пароль',
      );
      // END_BLOCK_SUBMIT_FAIL
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleSubmit */

  return (
    <div className="auth-page">
      <AuthPageHero />

      <section className="auth-page__panel" aria-labelledby="login-form-title">
        <div className="auth-page__form-wrap">
          <h2 id="login-form-title" className="auth-page__form-title">
            Вход
          </h2>
          <p className="auth-page__form-sub">
            Укажите email и пароль, чтобы открыть свои проекты.
          </p>

          {error ? (
            <div className="auth-page__alert" role="alert">
              {error}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} noValidate>
            <div className="auth-page__field">
              <label className="auth-page__label" htmlFor="login-email">
                Email
              </label>
              <input
                id="login-email"
                className="auth-page__input"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                required
              />
            </div>
            <div className="auth-page__field">
              <label className="auth-page__label" htmlFor="login-password">
                Пароль
              </label>
              <input
                id="login-password"
                className="auth-page__input"
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              className="auth-page__submit"
              disabled={loading}
            >
              {loading ? 'Вход…' : 'Войти'}
            </button>
          </form>

          {!flagsLoading && registrationEnabled && (
            <p className="auth-page__footer">
              Нет аккаунта?{' '}
              <RouterLink className="auth-page__link" to="/register">
                Создать аккаунт
              </RouterLink>
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
/* END_METHOD_LoginPage */
